import useAgentInterception from '../../../hooks/agentInterception/useAgentInterception.hooks';
import {
  AgentInterceptionType,
  messagingActions,
  TextFrameTransmissionMessage,
} from '../../../reducers/messagingSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import { selectActiveRestaurantDetails } from '../../../selectors/restaurant';
import { updateItemSlashCommandIdx } from '../../../reducers/menuSlice';
import { TopLevelMenuItem } from '../../../utils/menu';
import {
  formatCurrency,
  resolveDonationTotalPrompt,
} from '../../../utils/TTSMessage';
import { selectOrderValues } from '../../../selectors/order';
import {
  selectCartItemsQuantity,
  selectValidCartItems,
} from '../../../selectors/cart';
import { getSpokenTotalCartFingerprint } from '../../../utils/cart';
import { TOTAL_PLACEHOLDER } from '../../../constants';
import { SLASH_COMMAND_REMINDERS } from '../../../utils/constants';
import { orderActions } from '../../../reducers/orderSlice';

const useAutocompleteMessage = () => {
  const dispatch = useDispatch();
  const [currentMessage, setCurrentMessage] = useState('');
  const { sendAgentInterception } = useAgentInterception();
  const { restaurantName, restaurantCode } = useSelector(
    selectActiveRestaurantDetails
  );
  const { total } = useSelector(selectOrderValues);
  const validCartItems = useSelector(selectValidCartItems);
  const cartItemsQuantity = useSelector(selectCartItemsQuantity);
  const updateCurrentMessage = (message: string) => {
    setCurrentMessage(message);
  };
  const sendAgentInterventionEvent = (
    type: AgentInterceptionType,
    description?: string
  ) => {
    sendAgentInterception({
      data: {
        type,
        description,
      },
    });
  };
  const sendMessage = (message: string, metadata?: any, title?: string) => {
    message = message.split('{RESTAURANT_NAME}').join(restaurantName);

    if (message.includes(TOTAL_PLACEHOLDER) && total) {
      message = message
        .split(TOTAL_PLACEHOLDER)
        .join(formatCurrency(total, restaurantCode));
    }
    const payload: Partial<TextFrameTransmissionMessage> = {
      data: { payload: message, status: 'final', metadata },
    };
    dispatch(messagingActions.sendMessage(payload as any));
    sendAgentInterventionEvent(AgentInterceptionType.TTS_SENT);
  };

  /**
   * HITL-1570: send the donation upsell prompt, gated on the constraint's
   * `speak_total_in_upsell` condition. With the condition set: no check total
   * yet means the donation upsell is skipped entirely (nothing is sent, and
   * false is returned so the caller skips its side effects too); with a total,
   * `sendMessage` substitutes `{total}` and the spoken-total flag is recorded
   * so Complete can skip repeating it. Without the condition the prompt is
   * sent exactly as before. Returns whether the prompt was sent.
   */
  const sendDonationPromptMessage = (
    command: string,
    speakTotalInUpsell: boolean | undefined,
    metadata?: any
  ): boolean => {
    const { skip, totalSpoken } = resolveDonationTotalPrompt({
      command,
      speakTotalInUpsell,
      total,
    });
    if (skip) {
      return false;
    }
    if (totalSpoken) {
      // Capture the cart as it stands at the moment the total is spoken
      // (before the donation item is auto-added — '/upsell' items are
      // excluded from the fingerprint anyway). Complete honors the flag only
      // while the cart still matches.
      dispatch(
        orderActions.setTotalSpokenInUpsell({
          spoken: true,
          cartFingerprint: getSpokenTotalCartFingerprint(
            validCartItems,
            cartItemsQuantity
          ),
        })
      );
    }
    sendMessage(command, metadata);
    return true;
  };

  /**
   * HITL-1570: should a direct selection of this item be skipped outright?
   * True only for a donation item whose constraint declares
   * speak_total_in_upsell while no check total exists — the same gate the
   * reminder queue applies. Callers must then neither speak nor add the item.
   */
  const isDonationPromptSkipped = (item: {
    name?: string;
    speakTotalInUpsell?: boolean;
  }): boolean => {
    if ((item.name ?? '').split(':')[0] !== SLASH_COMMAND_REMINDERS.DONATION) {
      return false;
    }
    return resolveDonationTotalPrompt({
      command: '',
      speakTotalInUpsell: item.speakTotalInUpsell,
      total,
    }).skip;
  };

  /**
   * Returns whether a prompt was actually sent — false only when a gated
   * donation was skipped for lack of a check total, so the caller must skip
   * its own side effects (like adding the item to the cart) too.
   */
  const sendSlashCommandMessage = (
    item: TopLevelMenuItem,
    isUnifiedMenuEnabled: boolean
  ): boolean => {
    const itemName = item.name;
    let intent = '';
    let sendVal = '';
    if (isUnifiedMenuEnabled) {
      intent = itemName.replace('/', '');
      const { slashCommands, slashCommandIdx } = item;
      if (slashCommands && slashCommandIdx !== undefined) {
        sendVal = slashCommands[slashCommandIdx];
        const newIndex =
          slashCommandIdx === slashCommands.length - 1
            ? 0
            : slashCommandIdx + 1;
        dispatch(updateItemSlashCommandIdx({ itemName, newIndex }));
      }
    } else {
      [intent, sendVal] = itemName.replace('/', '').split(':');
    }
    // HITL-1570: the donation prompt is gated on the check total when its
    // constraint declares speak_total_in_upsell, and tracks whether the
    // total was spoken.
    if (itemName.split(':')[0] === SLASH_COMMAND_REMINDERS.DONATION) {
      return sendDonationPromptMessage(sendVal, item.speakTotalInUpsell, {
        intent,
      });
    }
    sendMessage(sendVal, { intent });
    return true;
  };

  return {
    currentMessage,
    updateCurrentMessage,
    sendMessage,
    sendDonationPromptMessage,
    isDonationPromptSkipped,
    sendAgentInterventionEvent,
    sendSlashCommandMessage,
  };
};

export default useAutocompleteMessage;
